import { Link } from 'react-router-dom';
import { SEO } from '../../components/seo/SEO';
import { defaultSeriesList } from '../../lib/seriesData';
import { getBaseUrl } from '../../lib/utils';

export default function SeriesPage() {
  const baseUrl = getBaseUrl();
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'シリーズ一覧 | つくってなんぼ',
    description: '進行中の制作シリーズ一覧。UI再現・制作フロー・サイト制作記録など。',
    url: `${baseUrl}/series`,
  };

  return (
    <>
      <SEO
        title="シリーズ一覧 | つくってなんぼ — AI実践メディア"
        description="進行中の制作シリーズ一覧。Spotify風UI再現・AI記事制作フロー・サイト制作記録など、複数回にわたる実践ログをまとめています。"
        canonical="/series"
        jsonLd={jsonLd}
      />

      <div className="min-h-screen bg-[#FDFAF6]">

        {/* Page header */}
        <div className="pt-24 sm:pt-32 pb-8 border-b border-[#E8E0D5]">
          <div className="max-w-2xl mx-auto px-4 sm:px-6">
            <p className="text-xs font-semibold tracking-widest uppercase text-[#5B7B6F] mb-3" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
              Series
            </p>
            <h1
              className="text-2xl sm:text-3xl font-bold text-[#2C2416] mb-3 leading-snug"
              style={{ fontFamily: "'Noto Serif JP', 'Georgia', serif", letterSpacing: '-0.01em' }}
            >
              シリーズ一覧
            </h1>
            <p className="text-sm text-[#7A6E62] leading-relaxed" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
              複数回にわたって追いかける制作テーマをまとめています。<br className="hidden sm:block" />
              進行中のシリーズは随時更新されます。
            </p>
          </div>
        </div>

        {/* Series list */}
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
          <div className="space-y-4 mb-12">
            {defaultSeriesList.map((series) => {
              const publishedCount = series.articles.filter((a) => a.status === 'published').length;
              const totalCount = series.articles.length;
              const progressPct = Math.round((publishedCount / totalCount) * 100);

              return (
                <Link
                  key={series.id}
                  to={`/series/${series.id}`}
                  className="block border border-[#E8E0D5] rounded-lg p-5 hover:border-[#C8BFB5] hover:bg-[#F8F3EC] transition-all cursor-pointer group bg-[#FDFAF6]"
                >
                  {/* Status + category */}
                  <div className="flex items-center gap-2 mb-3">
                    <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-[#5B7B6F] bg-[#EEF4F2] border border-[#5B7B6F]/20 px-2 py-0.5 rounded-full" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                      <span className="w-1.5 h-1.5 rounded-full bg-[#5B7B6F] inline-block"></span>
                      進行中
                    </span>
                    <span className="text-xs text-[#9A8E84]" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                      {series.category}
                    </span>
                  </div>

                  {/* Title */}
                  <h2
                    className="text-base sm:text-lg font-bold text-[#2C2416] leading-snug mb-2 group-hover:text-[#5B7B6F] transition-colors"
                    style={{ fontFamily: "'Noto Serif JP', serif" }}
                  >
                    {series.seriesName}
                  </h2>

                  {/* Description */}
                  <p className="text-xs text-[#7A6E62] leading-relaxed mb-4 line-clamp-2" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                    {series.description}
                  </p>

                  {/* Progress */}
                  <div className="mb-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-[#9A8E84]" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                        {publishedCount}/{totalCount}本公開
                      </span>
                      <span className="text-xs text-[#9A8E84]" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                        {progressPct}%
                      </span>
                    </div>
                    <div className="h-1 bg-[#EDE7DF] rounded-full overflow-hidden">
                      <div className="h-full bg-[#5B7B6F] rounded-full" style={{ width: `${progressPct}%` }}></div>
                    </div>
                  </div>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-1.5">
                    {series.tags.slice(0, 3).map((tag) => (
                      <span key={tag} className="text-[11px] text-[#9A8E84] bg-[#F0EBE3] px-2 py-0.5 rounded" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                        {tag}
                      </span>
                    ))}
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Bottom nav */}
          <div className="pt-6 border-t border-[#EDE7DF] flex flex-wrap gap-4">
            <Link to="/articles" className="inline-flex items-center gap-1.5 text-sm text-[#7A6E62] hover:text-[#2C2416] transition-colors cursor-pointer" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
              <i className="ri-article-line text-[#5B7B6F]"></i>
              記事一覧
            </Link>
            <span className="text-[#D4C9BE]">·</span>
            <Link to="/categories" className="inline-flex items-center gap-1.5 text-sm text-[#7A6E62] hover:text-[#2C2416] transition-colors cursor-pointer" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
              カテゴリ一覧
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
